/**
 * companyScopedViewState.js
 *
 * An AIP admin who switches company (see util/user/companySwitch) sees every
 * landing page scoped to that one company.  The landing pages also persist the
 * operator's last filter selection (customer, job number, operating area,
 * project type, search ...) in browser storage, and that selection is only
 * meaningful for the company it was made under.  Restoring it after a switch
 * hides every project card of the new company: e.g. a stored Job Number or
 * customer filter belonging to the previous company matches nothing, and the
 * per-page company filter is hidden while a company is active so the operator
 * cannot clear it.
 *
 * The persisted state is therefore tagged with the company it belongs to, and
 * discarded the first time a page loads under a different company.
 */

import { getActiveCompany, normalizeCompanyName } from '../../../../util/user/companySwitch';
import {
	ALL_PROJECTS_FILTERS_STORAGE_KEY,
	MY_PROJECTS_FILTERS_STORAGE_KEY
} from './projectFilterUtils';
import {
	ALL_PROJECTS_PAGINATION_STORAGE_KEY,
	MY_PROJECTS_PAGINATION_STORAGE_KEY,
	TRACKING_PAGINATION_STORAGE_KEY
} from './projectPageUtils';

/** CorrMonitor landing-page filter selection (localStorage). */
export const CORRMONITOR_LANDING_FILTERS_STORAGE_KEY = 'cg_corrmonitor_landing_filters';

/** Records the company the persisted view state below was saved under. */
export const VIEW_STATE_COMPANY_STORAGE_KEY = 'cg_view_state_company';

const SESSION_STATE_KEYS = [
	ALL_PROJECTS_FILTERS_STORAGE_KEY,
	MY_PROJECTS_FILTERS_STORAGE_KEY
];
const LOCAL_STATE_KEYS = [CORRMONITOR_LANDING_FILTERS_STORAGE_KEY];
const PAGINATION_STATE_KEYS = [
	ALL_PROJECTS_PAGINATION_STORAGE_KEY,
	MY_PROJECTS_PAGINATION_STORAGE_KEY,
	TRACKING_PAGINATION_STORAGE_KEY
];

const removeKeys = (storage, keys) => {
	keys.forEach(key => {
		try {
			storage.removeItem(key);
		} catch {
			// storage unavailable — nothing was restored from it either
		}
	});
};

/**
 * Drop the persisted filter selections when they were saved under a different
 * company than the one now active.  Cheap and idempotent, so each landing page
 * calls it before reading its stored filters.  Returns true when state was
 * discarded.
 */
export const resetViewStateForActiveCompany = () => {
	const current = normalizeCompanyName(getActiveCompany());
	let stored = null;
	try {
		stored = localStorage.getItem(VIEW_STATE_COMPANY_STORAGE_KEY);
	} catch {
		return false;
	}
	if (stored === current) {
		return false;
	}
	removeKeys(window.sessionStorage, SESSION_STATE_KEYS);
	removeKeys(localStorage, LOCAL_STATE_KEYS);
	try {
		localStorage.setItem(VIEW_STATE_COMPANY_STORAGE_KEY, current);
	} catch {
		// ignore — the next load simply clears again
	}
	return true;
};

/**
 * Forget every persisted landing-page selection (filters, search, page).  Called
 * on sign-out so the next sign-in - possibly a different operator, possibly the
 * same one acting as a different company - always starts from an unfiltered
 * view instead of inheriting a selection that hides every card.
 */
export const clearPersistedViewState = () => {
	removeKeys(window.sessionStorage, SESSION_STATE_KEYS);
	removeKeys(localStorage, [
		...LOCAL_STATE_KEYS,
		...PAGINATION_STATE_KEYS,
		VIEW_STATE_COMPANY_STORAGE_KEY
	]);
};
